import {
  ExternalLinkIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  RotateCcwIcon,
  Trash2Icon,
} from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import {
  Dialogo,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoPie,
  DialogoCerrar,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { TextoConFormato } from '@/components/ui/TextoConFormato'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { Confirmacion } from '@/features/admin/Confirmacion'
import { cn } from '@/lib/cn'
import type { Recurso } from '@/lib/schemas/recursos'
import { IconoRecurso } from './IconoRecurso'
import { ModalRecurso } from './ModalRecurso'
import { useBajaRecurso, useRecursos } from './api'

interface VistaRecursosProps {
  proyectoId: string
  esRastro: boolean
  /** Carpeta de Drive del cliente, para agregar enlaces con el selector (Fase 6). */
  carpetaDrive?: string | null
}

/** Cuántos enlaces se ven en la tarjeta; el resto, en el modal. */
const ENLACES_EN_TARJETA = 3

/**
 * P06 · pestaña Recursos (Fase 5b). Tarjetas tipo carpeta, ordenadas por
 * título (sin orden manual, decisión de Serena). Tocar una abre C12 con la
 * descripción y todos sus enlaces. RASTRO tiene un ⋮ chico en la esquina:
 * es la excepción a "sin menú en la tarjeta", porque acá no hay panel.
 */
export function VistaRecursos({
  proyectoId,
  esRastro,
  carpetaDrive = null,
}: VistaRecursosProps) {
  const avisar = useAvisar()
  const [verBaja, setVerBaja] = useState(false)
  const lista = useRecursos(proyectoId, esRastro && verBaja)
  const baja = useBajaRecurso()
  const [leyendo, setLeyendo] = useState<Recurso | null>(null)
  const [editando, setEditando] = useState<Recurso | null>(null)
  const [nuevo, setNuevo] = useState(false)
  const [dandoDeBaja, setDandoDeBaja] = useState<Recurso | null>(null)

  const recursos = lista.data ?? []

  return (
    <>
      {esRastro && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <Boton
            variante="sutil"
            tamano="chico"
            aria-pressed={verBaja}
            onClick={() => {
              setVerBaja((x) => !x)
            }}
          >
            {verBaja ? 'Ocultar dados de baja' : 'Mostrar dados de baja'}
          </Boton>
          <Boton
            tamano="chico"
            className="hidden sm:inline-flex"
            onClick={() => {
              setNuevo(true)
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Nuevo recurso
          </Boton>
        </div>
      )}

      {lista.isPending ? (
        <p className="py-10 text-center text-sm text-tinta-suave">Cargando…</p>
      ) : recursos.length === 0 ? (
        <section className="rounded-tarjeta bg-tarjeta px-6 py-12 text-center shadow-tarjeta">
          <h2 className="text-lg font-semibold">
            {esRastro
              ? 'Este cliente no tiene recursos.'
              : 'Todavía no hay recursos cargados.'}
          </h2>
          {esRastro && (
            <>
              <p className="mt-1 text-sm text-tinta-suave">Cargá el primero.</p>
              <Boton
                className="mt-4"
                onClick={() => {
                  setNuevo(true)
                }}
              >
                <PlusIcon className="size-4" aria-hidden />
                Nuevo recurso
              </Boton>
            </>
          )}
        </section>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {recursos.map((r) => (
            <li key={r.id}>
              <TarjetaRecurso
                recurso={r}
                esRastro={esRastro}
                alAbrir={() => {
                  setLeyendo(r)
                }}
                alEditar={() => {
                  setEditando(r)
                }}
                alDarDeBaja={() => {
                  setDandoDeBaja(r)
                }}
                alRestaurar={() => {
                  void baja.mutateAsync({ id: r.id, restaurar: true }).then(() => {
                    avisar('Recurso restaurado')
                  })
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {esRastro && (
        <div className="fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-30 sm:hidden">
          <Boton
            tamano="icono"
            className="size-14 shadow-tarjeta"
            aria-label="Nuevo recurso"
            onClick={() => {
              setNuevo(true)
            }}
          >
            <PlusIcon className="size-6" aria-hidden />
          </Boton>
        </div>
      )}

      {leyendo && (
        <LecturaRecurso
          recurso={leyendo}
          esRastro={esRastro}
          alCerrar={() => {
            setLeyendo(null)
          }}
          alEditar={() => {
            setEditando(leyendo)
            setLeyendo(null)
          }}
        />
      )}

      <ModalRecurso
        abierto={nuevo || editando !== null}
        alCerrar={() => {
          setNuevo(false)
          setEditando(null)
        }}
        proyectoId={proyectoId}
        recurso={editando}
        carpetaDrive={carpetaDrive}
      />

      <Confirmacion
        abierto={dandoDeBaja !== null}
        alCerrar={() => {
          setDandoDeBaja(null)
        }}
        titulo="Dar de baja el recurso"
        texto='El recurso deja de verse para el cliente. Podés restaurarlo con "Mostrar dados de baja".'
        confirmar="Dar de baja"
        peligro
        alConfirmar={async () => {
          if (!dandoDeBaja) return
          await baja.mutateAsync({ id: dandoDeBaja.id, restaurar: false })
          avisar('Recurso dado de baja')
        }}
      />
    </>
  )
}

function TarjetaRecurso({
  recurso: r,
  esRastro,
  alAbrir,
  alEditar,
  alDarDeBaja,
  alRestaurar,
}: {
  recurso: Recurso
  esRastro: boolean
  alAbrir: () => void
  alEditar: () => void
  alDarDeBaja: () => void
  alRestaurar: () => void
}) {
  const visibles = r.enlaces.slice(0, ENLACES_EN_TARJETA)
  const resto = r.enlaces.length - visibles.length
  return (
    <article
      data-recurso={r.titulo}
      className={cn(
        // Se levanta al pasar el mouse, como las tarjetas de la grilla (DEC-23).
        'al-pasar relative flex h-full min-h-44 flex-col gap-2 rounded-tarjeta bg-tarjeta p-3.5 shadow-tarjeta sm:p-4',
        r.dadoDeBaja && 'opacity-60',
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-borrador text-borrador-tinta sm:size-12">
        <IconoRecurso icono={r.icono} className="size-5 sm:size-6" />
      </span>
      <h2 className="text-sm leading-snug font-semibold sm:text-base">
        <button
          type="button"
          onClick={alAbrir}
          className="text-left after:absolute after:inset-0 after:rounded-tarjeta focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-tinta"
        >
          {r.titulo}
        </button>
      </h2>
      {r.dadoDeBaja && <p className="text-xs text-tinta-suave">Dado de baja</p>}
      <ul className="relative z-10 flex max-w-full flex-col gap-1.5 self-start text-xs sm:text-sm">
        {visibles.map((e, i) => (
          <li key={i} className="truncate">
            <a
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              // Los enlaces van en el azul del calendario (DEC-24).
              className="inline-flex max-w-full items-center gap-1 text-hoy underline-offset-2 hover:underline"
            >
              <ExternalLinkIcon className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{e.nombre}</span>
            </a>
          </li>
        ))}
        {resto > 0 && (
          <li className="text-tinta-suave">
            y {resto} {resto === 1 ? 'enlace más' : 'enlaces más'}
          </li>
        )}
      </ul>

      {esRastro && (
        <div className="absolute top-2 right-2 z-20">
          <MenuDesplegable>
            <MenuDisparador asChild>
              <Boton
                variante="sutil"
                tamano="icono"
                aria-label={`Opciones de ${r.titulo}`}
              >
                <MoreVerticalIcon className="size-4" aria-hidden />
              </Boton>
            </MenuDisparador>
            <MenuContenido align="end">
              {r.dadoDeBaja ? (
                <MenuItem onSelect={alRestaurar}>
                  <RotateCcwIcon className="size-4" aria-hidden />
                  Restaurar
                </MenuItem>
              ) : (
                <>
                  <MenuItem onSelect={alEditar}>
                    <PencilIcon className="size-4" aria-hidden />
                    Editar
                  </MenuItem>
                  <MenuItem peligro onSelect={alDarDeBaja}>
                    <Trash2Icon className="size-4" aria-hidden />
                    Dar de baja
                  </MenuItem>
                </>
              )}
            </MenuContenido>
          </MenuDesplegable>
        </div>
      )}
    </article>
  )
}

/** C12 · el recurso completo: descripción y todos sus enlaces. */
function LecturaRecurso({
  recurso: r,
  esRastro,
  alCerrar,
  alEditar,
}: {
  recurso: Recurso
  esRastro: boolean
  alCerrar: () => void
  alEditar: () => void
}) {
  return (
    <Dialogo
      open
      onOpenChange={(o) => {
        if (!o) alCerrar()
      }}
    >
      <DialogoContenido>
        <DialogoEncabezado className="flex-row items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-borrador text-borrador-tinta">
            <IconoRecurso icono={r.icono} className="size-5" />
          </span>
          <div className="min-w-0">
            <DialogoTitulo className="text-xl font-semibold tracking-tight">
              {r.titulo}
            </DialogoTitulo>
            <DialogoDescripcion className="sr-only">
              Recurso del cliente
            </DialogoDescripcion>
          </div>
        </DialogoEncabezado>
        <DialogoCuerpo className="flex flex-col gap-4">
          {r.descripcion && <TextoConFormato texto={r.descripcion} />}
          {r.enlaces.length > 0 ? (
            <ul className="flex flex-col divide-y divide-borde rounded-tarjeta border border-borde">
              {r.enlaces.map((e, i) => (
                <li key={i}>
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-12 items-center gap-3 px-4 py-2.5 text-sm hover:bg-lienzo"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {e.nombre}
                    </span>
                    <ExternalLinkIcon
                      className="size-4 shrink-0 text-tinta-suave"
                      aria-hidden
                    />
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            !r.descripcion && (
              <p className="text-sm text-tinta-suave">Sin enlaces todavía.</p>
            )
          )}
        </DialogoCuerpo>
        <DialogoPie>
          <DialogoCerrar asChild>
            <Boton variante="secundario">Cerrar</Boton>
          </DialogoCerrar>
          {esRastro && !r.dadoDeBaja && (
            <Boton onClick={alEditar}>
              <PencilIcon className="size-4" aria-hidden />
              Editar
            </Boton>
          )}
        </DialogoPie>
      </DialogoContenido>
    </Dialogo>
  )
}
